import type { Cents } from "../domain/money";
import { formatCents } from "../domain/money";
import { formatX100 } from "../domain/measurement";
import type { ProjectProductSelection } from "../domain/catalog";
import { computePurchaseQuantity } from "./purchaseQuantity";
import { distributeProportionally } from "./purchaseMath";

/**
 * Bill of Materials (Phase 6) — a DERIVED view, never a table.
 *
 * Aggregate-then-round (approved Decision 1): component construction
 * requirements that share a compatible selection are summed, converted
 * to purchase units ONCE, and the group's cost is allocated back to the
 * components proportionally to construction demand with largest-remainder
 * cents (Decision 2) so Σ component lines === BOM row cost exactly.
 *
 * Aggregation key (accounting truth over aggressive consolidation):
 *   product + purchase unit + coverage snapshot + price snapshot +
 *   tax-inclusive flag. Selections with round_separately or a purchase-
 *   quantity override form their own group.
 */
export interface BomDemandInput {
  componentId: string;
  componentName: string;
  instanceId: string;
  featureId: string;
  featureName: string;
  /** Room/floor/project context label for traceability. */
  scopeLabel: string;
  /** Effective construction requirement (Phase 4), ×100, in `unit`. */
  requiredX100: number;
  unit: string;
  taxable: boolean;
  /** Phase 4 estimating amount (used when no product is selected). */
  estimatingAmountCents: Cents;
  selection: ProjectProductSelection | null;
}

export interface BomComponentShare {
  componentId: string;
  componentName: string;
  featureId: string;
  featureName: string;
  scopeLabel: string;
  requiredX100: number;
  /** Share of the group's merchandise cost (pre-tax). */
  subtotalCents: Cents;
  estimatedTaxCents: Cents;
  /** subtotal + tax — written to the generated Proposed Cost Line. */
  amountCents: Cents;
  explanation: string[];
}

export interface BomGroup {
  key: string;
  productId: string | null;
  productName: string;
  supplierName: string;
  unit: string;
  purchaseUnit: string | null;
  coveragePerUnitX100: number | null;
  priceCentsSnapshot: Cents | null;
  priceIncludesTax: boolean;
  priceSource: string | null;
  roundSeparately: boolean;
  requiredX100: number;
  rawUnitsX100: number;
  calculatedPurchaseUnits: number;
  purchaseQtyOverride: number | null;
  effectivePurchaseUnits: number;
  purchasedCoverageX100: number;
  roundingExcessX100: number;
  /** effectiveUnits × price (pre-tax merchandise). */
  subtotalCents: Cents;
  estimatedTaxCents: Cents;
  totalCents: Cents;
  needsSelection: boolean;
  components: BomComponentShare[];
}

export interface BomResult {
  groups: BomGroup[];
  totalCents: Cents;
  needsSelectionCount: number;
}

function roundDiv(n: number, d: number): number {
  return Math.floor((n + d / 2) / d);
}

export function selectionKey(s: ProjectProductSelection): string {
  if (s.roundSeparately || s.purchaseQtyOverride !== null) return `solo:${s.id}`;
  return [
    s.productId,
    s.purchaseUnitSnapshot,
    s.coveragePerUnitX100Snapshot,
    s.coverageUnitSnapshot,
    s.priceCentsSnapshot,
    s.priceIncludesTaxSnapshot ? 1 : 0,
  ].join("|");
}

export function computeBom(demands: BomDemandInput[], estimatingTaxPctX100: number | null): BomResult {
  const byKey = new Map<string, BomDemandInput[]>();
  for (const d of demands) {
    const key = d.selection ? selectionKey(d.selection) : `unselected:${d.componentId}`;
    const arr = byKey.get(key) ?? [];
    arr.push(d);
    byKey.set(key, arr);
  }

  const groups: BomGroup[] = [];
  for (const [key, members] of byKey) {
    const sel = members[0].selection;
    const required = members.reduce((s, m) => s + m.requiredX100, 0);

    if (!sel) {
      const m = members[0];
      groups.push({
        key,
        productId: null,
        productName: m.componentName,
        supplierName: "",
        unit: m.unit,
        purchaseUnit: null,
        coveragePerUnitX100: null,
        priceCentsSnapshot: null,
        priceIncludesTax: false,
        priceSource: null,
        roundSeparately: false,
        requiredX100: required,
        rawUnitsX100: 0,
        calculatedPurchaseUnits: 0,
        purchaseQtyOverride: null,
        effectivePurchaseUnits: 0,
        purchasedCoverageX100: 0,
        roundingExcessX100: 0,
        subtotalCents: m.estimatingAmountCents,
        estimatedTaxCents: 0,
        totalCents: m.estimatingAmountCents,
        needsSelection: true,
        components: [
          {
            componentId: m.componentId,
            componentName: m.componentName,
            featureId: m.featureId,
            featureName: m.featureName,
            scopeLabel: m.scopeLabel,
            requiredX100: m.requiredX100,
            subtotalCents: m.estimatingAmountCents,
            estimatedTaxCents: 0,
            amountCents: m.estimatingAmountCents,
            explanation: ["No product selected — Phase 4 estimating basis retained."],
          },
        ],
      });
      continue;
    }

    const pq = computePurchaseQuantity(required, sel.coveragePerUnitX100Snapshot);
    const effectiveUnits = sel.purchaseQtyOverride ?? pq.purchaseUnits;
    const subtotal = effectiveUnits * sel.priceCentsSnapshot;
    const shares = distributeProportionally(subtotal, members.map((m) => m.requiredX100));
    const applyTax =
      !sel.priceIncludesTaxSnapshot && estimatingTaxPctX100 !== null && estimatingTaxPctX100 > 0;
    const components: BomComponentShare[] = members.map((m, i) => {
      const tax = applyTax && m.taxable ? roundDiv(shares[i] * estimatingTaxPctX100!, 10000) : 0;
      const explanation = [
        `Construction requirement ${formatX100(m.requiredX100)} ${m.unit} (of group total ${formatX100(required)} ${m.unit})`,
        `Group purchase: ${formatX100(required)} ${m.unit} ÷ ${formatX100(sel.coveragePerUnitX100Snapshot)} ${sel.coverageUnitSnapshot}/${sel.purchaseUnitSnapshot} = ${formatX100(pq.rawUnitsX100)} → ${pq.purchaseUnits} ${sel.purchaseUnitSnapshot}` +
          (sel.purchaseQtyOverride !== null ? ` (user override: ${effectiveUnits})` : ""),
        `Group cost ${effectiveUnits} × ${formatCents(sel.priceCentsSnapshot)}/${sel.purchaseUnitSnapshot} = ${formatCents(subtotal)} (${sel.priceSourceSnapshot}${sel.priceIncludesTaxSnapshot ? ", tax-inclusive" : ", pre-tax"})`,
        `Proportional share ${formatX100(m.requiredX100)}/${formatX100(required)} = ${formatCents(shares[i])}` +
          (members.length > 1 ? " (largest-remainder cents)" : ""),
      ];
      if (tax > 0) explanation.push(`+ estimating tax ${formatX100(estimatingTaxPctX100!)}% = ${formatCents(tax)}`);
      else if (sel.priceIncludesTaxSnapshot && m.taxable) explanation.push("Price is tax-inclusive — no estimating tax applied.");
      return {
        componentId: m.componentId,
        componentName: m.componentName,
        featureId: m.featureId,
        featureName: m.featureName,
        scopeLabel: m.scopeLabel,
        requiredX100: m.requiredX100,
        subtotalCents: shares[i],
        estimatedTaxCents: tax,
        amountCents: shares[i] + tax,
        explanation,
      };
    });
    const tax = components.reduce((s, c) => s + c.estimatedTaxCents, 0);
    groups.push({
      key,
      productId: sel.productId,
      productName: sel.productNameSnapshot,
      supplierName: sel.supplierNameSnapshot,
      unit: members[0].unit,
      purchaseUnit: sel.purchaseUnitSnapshot,
      coveragePerUnitX100: sel.coveragePerUnitX100Snapshot,
      priceCentsSnapshot: sel.priceCentsSnapshot,
      priceIncludesTax: sel.priceIncludesTaxSnapshot,
      priceSource: sel.priceSourceSnapshot,
      roundSeparately: sel.roundSeparately,
      requiredX100: required,
      rawUnitsX100: pq.rawUnitsX100,
      calculatedPurchaseUnits: pq.purchaseUnits,
      purchaseQtyOverride: sel.purchaseQtyOverride,
      effectivePurchaseUnits: effectiveUnits,
      purchasedCoverageX100: effectiveUnits * sel.coveragePerUnitX100Snapshot,
      roundingExcessX100: effectiveUnits * sel.coveragePerUnitX100Snapshot - required,
      subtotalCents: subtotal,
      estimatedTaxCents: tax,
      totalCents: subtotal + tax,
      needsSelection: false,
      components,
    });
  }

  groups.sort((a, b) => a.productName.localeCompare(b.productName));
  return {
    groups,
    totalCents: groups.reduce((s, g) => s + g.totalCents, 0),
    needsSelectionCount: groups.filter((g) => g.needsSelection).length,
  };
}

/** Equivalent construction-unit price for a purchase: net merchandise ÷ (qty × coverage). */
export function equivalentUnitPriceCents(
  netMerchandiseCents: Cents,
  purchasedUnitsX100: number,
  coveragePerUnitX100: number,
): Cents | null {
  const coverageX10000 = purchasedUnitsX100 * coveragePerUnitX100; // (units×100)(cov×100)
  if (coverageX10000 <= 0) return null;
  return roundDiv(netMerchandiseCents * 10000, coverageX10000);
}
